import { memo, type ReactNode } from 'react';

import { ClipType } from '@air/api/types';
import { BoardIcon, GlobeIcon, Settings2Icon, SquareArrowOutUpRightIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';

import { WorkspaceAgentAssetPreviewMediaFallback } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentAssetPreviewMediaFallback';
import { WorkspaceAgentPreviewCardFrame } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentPreviewCardFrame';
import { WorkspaceAgentPreviewCardMetaRow } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentPreviewCardMetaRow';
import { WorkspaceAgentShareLinkPreviewUrlRow } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentShareLinkPreviewUrlRow';
import { type WorkspaceAgentShareLinkMonikerPreviewMedia } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentShareLinkMonikerPreviewMedia';

export type WorkspaceAgentShareLinkPreviewCardProps = {
  closeButton?: ReactNode;
  /** Public URL with the scheme and a leading `app.` already removed. */
  displayUrl: string;
  onCopyLink: () => void;
  /**
   * Opens the public share URL in a new tab. Preview metadata may describe a board or asset target, but
   * this control is the visitor-facing link, not in-product navigation.
   */
  onOpenLink: () => void;
  /** Opens that target's share-settings modal. Absent when the short url has no settings surface. */
  onOpenSettings?: () => void;
  previewMedia: WorkspaceAgentShareLinkMonikerPreviewMedia | null;
  testId?: string;
  /** Board or asset name when `previewMedia` is set; heading for text-only form/canvas/unknown links. */
  title: string;
};

const getShareLinkPreviewKindLabel = (previewMedia: WorkspaceAgentShareLinkMonikerPreviewMedia): string => {
  switch (previewMedia.kind) {
    case 'board':
      return 'Share link · Board';
    case 'asset':
      return 'Share link · Asset';
  }
};

const getShareLinkPreviewMediaFallback = (previewMedia: WorkspaceAgentShareLinkMonikerPreviewMedia): ReactNode => {
  switch (previewMedia.kind) {
    case 'board':
      return <BoardIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size={32} />;
    case 'asset':
      return <WorkspaceAgentAssetPreviewMediaFallback ext={previewMedia.ext} type={previewMedia.clipType} />;
  }
};

type ShareLinkVersionChipProps = {
  versionLabel: string;
};

const ShareLinkVersionChip = memo(({ versionLabel }: ShareLinkVersionChipProps) => (
  <span
    className="inline-flex h-4 shrink-0 items-center rounded-2 ads:bg-ads-gray-4 bg-gray-4 px-1 font-medium ads:text-primary text-10 text-gray-12 leading-none"
    data-testid="WORKSPACE_AGENT_SHARE_LINK_PREVIEW_VERSION"
  >
    {versionLabel}
  </span>
));

ShareLinkVersionChip.displayName = 'ShareLinkVersionChip';

/** Share-link popover body. The media and title are not a button; copy lives on the URL row. */
export const WorkspaceAgentShareLinkPreviewCard = memo(
  ({
    closeButton,
    displayUrl,
    onCopyLink,
    onOpenLink,
    onOpenSettings,
    previewMedia,
    testId = 'WORKSPACE_AGENT_SHARE_LINK_PREVIEW_CARD',
    title,
  }: WorkspaceAgentShareLinkPreviewCardProps) => {
    const footer = (
      <div className="flex items-center gap-2">
        <Button
          appearance="outline"
          className="w-fit"
          color="gray"
          data-testid="WORKSPACE_AGENT_SHARE_LINK_PREVIEW_OPEN_LINK"
          onClick={onOpenLink}
          prefix={<SquareArrowOutUpRightIcon aria-hidden size="xs" />}
          size="small"
        >
          Open link
        </Button>
        {onOpenSettings ? (
          <Button
            appearance="outline"
            className="w-fit"
            color="gray"
            data-testid="WORKSPACE_AGENT_SHARE_LINK_PREVIEW_SETTINGS"
            onClick={onOpenSettings}
            prefix={<Settings2Icon aria-hidden size="xs" />}
            size="small"
          >
            Settings
          </Button>
        ) : null}
      </div>
    );
    const urlRow = <WorkspaceAgentShareLinkPreviewUrlRow displayUrl={displayUrl} onCopyLink={onCopyLink} />;

    if (previewMedia) {
      const kindLabel = getShareLinkPreviewKindLabel(previewMedia);

      return (
        <WorkspaceAgentPreviewCardFrame
          closeButton={closeButton}
          footer={footer}
          // Only over a still: on the bare type glyph the scrim would bury what it sits on.
          hasPlayOverlay={
            previewMedia.kind === 'asset' && previewMedia.clipType === ClipType.video && !!previewMedia.thumbnailUrl
          }
          locationLabel={null}
          locationTitle={null}
          mediaFallback={getShareLinkPreviewMediaFallback(previewMedia)}
          mediaImageUrl={previewMedia.thumbnailUrl}
          testId={testId}
          title={title}
          titleAdornment={
            previewMedia.kind === 'asset' ? (
              <ShareLinkVersionChip versionLabel={previewMedia.versionLabel} />
            ) : undefined
          }
        >
          <WorkspaceAgentPreviewCardMetaRow
            icon={<GlobeIcon aria-hidden size="xs" />}
            testId="WORKSPACE_AGENT_SHARE_LINK_PREVIEW_KIND"
          >
            {kindLabel}
          </WorkspaceAgentPreviewCardMetaRow>
          {urlRow}
        </WorkspaceAgentPreviewCardFrame>
      );
    }

    return (
      <div
        aria-atomic="false"
        className="relative w-max min-w-[200px] max-w-60 overflow-hidden rounded-8"
        data-testid={testId}
        role="status"
      >
        {closeButton ? <span className="absolute top-1.5 right-1.5 z-1">{closeButton}</span> : null}
        <div className="flex flex-col gap-1 px-2.5 py-2 pr-9">
          <span
            className="min-w-0 break-all font-medium ads:text-primary text-12 text-gray-12 leading-4"
            data-testid="WORKSPACE_AGENT_SHARE_LINK_PREVIEW_TITLE"
          >
            {title}
          </span>
          <WorkspaceAgentPreviewCardMetaRow icon={<GlobeIcon aria-hidden size="xs" />}>
            Share link
          </WorkspaceAgentPreviewCardMetaRow>
          {urlRow}
        </div>
        <div className="flex items-center px-2.5 pb-2.5">{footer}</div>
      </div>
    );
  },
);

WorkspaceAgentShareLinkPreviewCard.displayName = 'WorkspaceAgentShareLinkPreviewCard';
